import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useTheme, type ThemePreference } from '@/app/ThemeProvider'
import { request } from '@/api/client'
import { errorMessage } from '@/api/messages'
import { InlineEdit } from '@/components/InlineEdit'
import { queryKeys } from '@/api/queryKeys'
import type { MeResponse } from '@/api/types'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { Page, PageHeader } from '@/components/Page'
import { Icon } from '@/components/Icon'
import { SegmentedControl } from '@/components/SegmentedControl'
import { SettingRow } from '@/components/SettingRow'
import { useToast } from '@/components/Toast'
import { useSession } from '@/features/auth/SessionProvider'

const THEMES: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
]

const SHORTCUTS: [string, string][] = [
  ['/', 'Focus the search field'],
  ['Ctrl / Cmd + Enter', 'Save the consultation you are writing'],
  ['Esc', 'Close the attachment viewer or a menu'],
  ['← →', 'Move between attachments'],
]

export function SettingsScreen() {
  const { user, signOut, can } = useSession()

  /** The clinic owns its data. This is also the honest answer to "what happens
   *  if we stop using you". Admin-only, audited and rate-limited server side. */
  async function exportRegister() {
    try {
      const ndjson = await request<string>('/v1/export')
      const blob = new Blob([typeof ndjson === 'string' ? ndjson : JSON.stringify(ndjson)], {
        type: 'application/x-ndjson',
      })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `clinic-export-${new Date().toISOString().slice(0, 10)}.ndjson`
      link.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.showError('Export failed. Administrators only.')
    }
  }
  const { preference, resolved, setPreference } = useTheme()
  const navigate = useNavigate()
  const toast = useToast()

  const revokeAll = useMutation({
    mutationFn: () => request<{ revoked_sessions: number }>('/v1/auth/session/revoke-all', { method: 'POST' }),
    onSuccess: (result) =>
      toast.show(
        result.revoked_sessions === 1
          ? 'Signed out of 1 other session'
          : `Signed out of ${result.revoked_sessions} sessions`,
      ),
    onError: () => toast.showError('Could not reach the server.'),
  })

  return (
    <Page>
      <PageHeader title="Settings" description="Your account and how this device behaves." />

      <div className="grid content-start gap-4">
        <Card title="Appearance" flush>
          <SettingRow
            label="Theme"
            help={`Light matches the clinic's printed forms. Dark is easier in a dim consulting room at night. "System" follows this device — currently ${resolved}.`}
          >
            <SegmentedControl
              label="Theme"
              options={THEMES}
              value={preference}
              onChange={setPreference}
            />
          </SettingRow>
        </Card>

        {user && <AccountCard user={user} />}

        <Card title="Security" flush>
          <SettingRow
            label="Sign out everywhere"
            help="Ends every other session on every device. Use it if a phone is lost — this is the control that actually matters, not changing a password."
          >
            <Button
              variant="secondary"
              loading={revokeAll.isPending}
              onClick={() => revokeAll.mutate()}
            >
              Sign out other devices
            </Button>
          </SettingRow>
          <SettingRow
            label="Sign out of this device"
            help="Clears every draft and cached record stored here. Do it before handing the device to someone else."
          >
            <Button
              variant="destructive"
              onClick={async () => {
                const reachedServer = await signOut()
                if (!reachedServer) {
                  toast.showError(
                    'Signed out here, but the server could not be reached. Sign out everywhere from another device when you can.',
                  )
                }
                navigate('/', { replace: true })
              }}
            >
              <Icon name="signOut" />
              Log out
            </Button>
          </SettingRow>
        </Card>

        {can('export:all') && (
          <Card title="Data">
            <p className="mb-4 max-w-prose text-sm leading-normal text-fg-muted">
              The whole register as NDJSON — patients, consultations, prescriptions, test
              results and document metadata. Every export is recorded in the audit log.
            </p>
            <Button variant="secondary" onClick={exportRegister}>
              <Icon name="save" />
              Export register
            </Button>
          </Card>
        )}

        <Card title="Keyboard shortcuts" flush>
          {SHORTCUTS.map(([keys, description]) => (
            <SettingRow
              key={keys}
              label={<kbd className="font-mono text-xs text-fg">{keys}</kbd>}
              help={description}
            />
          ))}
        </Card>
      </div>
    </Page>
  )
}

/**
 * Your own details, each editable where it is read.
 *
 * Editable here rather than only by an administrator: a typo in a name is
 * printed on every prescription until somebody fixes it, and the person who
 * spots it is the person it belongs to. The role is deliberately absent — it is
 * a decision made about you, not by you, and showing it in a form invites the
 * question of why it cannot be changed.
 */
function AccountCard({ user }: { user: MeResponse }) {
  const queryClient = useQueryClient()

  /** One field at a time: the rest of the row is sent back as it stands, so a
   *  save never silently reverts something edited a moment earlier. */
  async function save(changes: Partial<MeResponse>) {
    try {
      const updated = await request<MeResponse>('/v1/auth/me', {
        method: 'PATCH',
        body: {
          full_name: user.full_name,
          email: user.email,
          registration_number: user.registration_number,
          ...changes,
        },
      })
      queryClient.setQueryData(queryKeys.me, updated)
    } catch (caught) {
      // Rethrown, not toasted: the field puts its old value back and shows the
      // reason beneath itself, which is where the eye already is. A toast as
      // well would say the same thing twice.
      throw new Error(errorMessage(caught))
    }
  }

  return (
    <Card title="Account" flush>
      <SettingRow label="Name">
        <InlineEdit
          label="Name"
          required
          autoCapitalize="words"
          value={user.full_name}
          onSave={(full_name) => save({ full_name })}
        />
      </SettingRow>
      <SettingRow label="Email" help="This is what you sign in with.">
        <InlineEdit
          label="Email"
          type="email"
          required
          value={user.email}
          onSave={(email) => save({ email })}
        />
      </SettingRow>
      <SettingRow label="Registration number" help="Printed on every prescription.">
        <InlineEdit
          label="Registration number"
          placeholder="Not recorded"
          value={user.registration_number ?? ''}
          onSave={(registration_number) => save({ registration_number: registration_number || null })}
        />
      </SettingRow>
    </Card>
  )
}
